import React, { useEffect, useState } from 'react'
import { ActionableTable } from '../../../Table/ActionableTable'
import { useTranslation } from 'react-i18next'
import { useLoadingIds } from '../../../../hooks/useLoadingIds'
import { useAppSnackbar } from '../../../../hooks/useAppSnackbar'
import { OrderByDirection } from '../../../../swagger'
import useLoadingContext from '../../../../hooks/useLoadingContext'
import getOrderByClause from '../../../../utils/getOrderByClause'
import { paymentsApi } from '../../../../api/swagger'
import { usePagination } from '../../../../hooks/usePagination'
import { getLcdLicensingPaymentHistoryColumns } from './columns'
import { getLcdLicensingPaymentHistoryRowActions } from './rowActions'
import { getLcdLicensingPaymentHistoryRows } from './rows'
import TransactionModal from '../../../Modals/TransactionModal'
import ProgramOption from '../../../Interfaces/ProgramOption'
import { Payment, Payment as PaymentMade } from '../../../Interfaces/Payment'
import { useAuth } from '../../../Routes/AuthProvider'
import { Transaction } from '../BillingHistorySummaryTable'
import ViewBillingInfoModal from '../../../Families/ViewBillingInfoModal'
import { reinterpretYearMonthDayAsLocalTime } from '../../../../utils/reinterpretYearMonthDayAsLocalTime'

interface LcdLicensingPaymentHistoryProps {
  /**
   * Available programs when adding or editing a transaction for licensing
   * @type ProgramOption Array
   */
  licensingProgramOptions?: ProgramOption[]
}

const LcdLicensingPaymentHistory: React.FC<LcdLicensingPaymentHistoryProps> = ({
  licensingProgramOptions = [],
}) => {
  const { t } = useTranslation()
  const { handleError } = useAppSnackbar()
  const { permissionAbility } = useAuth()
  const canEditPayments =
    permissionAbility.can('updateEnrollmentPayment', 'Payment') ||
    permissionAbility.can('updateLicensingPayment', 'Payment')

  const {
    PaymentApi: {
      fetchLicensingLcdPayments: fetchLicensingLcdPaymentsLoadingId,
    },
  } = useLoadingIds()

  const [paymentHistory, setPaymentHistory] = useState<PaymentMade[]>([])
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState<PaymentMade>()
  const [selectedPrograms, setSelectedPrograms] = useState<ProgramOption[]>([])
  const { pagination, setPage, setPageSize, setTotalCount } = usePagination({
    initialOrderBy: [{ begunAt: OrderByDirection.Desc }],
  })
  const [billingHistoryViewModalOpen, setBillingHistoryViewModalOpen] =
    useState(false)
  const [transaction, setTransaction] = useState<Transaction>({
    paymentMethod: '',
    date: new Date(),
    status: '',
    amount: '',
    orderId: '',
    parentId: -1,
    studentNames: [''],
  })

  const noResultsMessage = t(
    'LcdLicensingPaymentHistory.NoTransactionHistory',
    'No transaction history'
  )

  const errorMessage = t(
    'LcdLicensingPaymentHistory.Error',
    'An error occurred while attempting to load the payment history'
  )

  const fetchPaymentHistory = async () => {
    try {
      const data = await paymentsApi.fetchLicensingLcdPayments({
        page: pagination.page,
        pageSize: pagination.pageSize,
        orderBy: getOrderByClause(pagination.orderBy),
      })

      setPaymentHistory(
        data.data.map((licensingHistory) => {
          const paymentHistory: Payment = {
            ...licensingHistory,
            paymentsMade: [
              {
                ...licensingHistory.licensingPayment,
                paymentKey:
                  licensingHistory.licensingPayment.licensingPaymentKey,
                studentKey: -1,
              },
            ],
          }
          return paymentHistory
        }) ?? []
      )
      setTotalCount(data.pagination?.totalCount ?? 0)
    } catch (error) {
      await handleError(error, errorMessage)
    }
  }

  const { triggerFetch, isLoading } = useLoadingContext({
    asyncFunction: fetchPaymentHistory,
    loadingId: fetchLicensingLcdPaymentsLoadingId,
  })

  useEffect(() => {
    triggerFetch()
  }, [pagination.page, pagination.pageSize, triggerFetch])

  const handleEdit = (payment: Payment) => {
    setSelectedPayment(payment)
    setSelectedPrograms(
      licensingProgramOptions?.filter(
        (licensingProgramOption) =>
          payment.paymentsMade[0].programKey ===
          licensingProgramOption.programKey
      ) ?? []
    )
    setIsTransactionModalOpen(!isTransactionModalOpen)
  }

  const handleMoreInfo = (
    paymentMethod: string,
    date: Date,
    status: string,
    amount: string,
    orderId: string,
    parentKey: number,
    studentNames: string[]
  ) => {
    setTransaction({
      paymentMethod,
      date,
      status,
      amount,
      orderId,
      parentId: parentKey,
      studentNames,
    })
    setBillingHistoryViewModalOpen(true)
  }

  const columns = getLcdLicensingPaymentHistoryColumns(t)
  const rows = getLcdLicensingPaymentHistoryRows(t, paymentHistory)
  const rowActions = getLcdLicensingPaymentHistoryRowActions({
    t,
    handleEdit,
    canEditPayments,
    handleMoreInfo,
  })

  return (
    <>
      {isTransactionModalOpen && (
        <TransactionModal
          isOpen
          onClose={() => setIsTransactionModalOpen(false)}
          payment={selectedPayment}
          isLicensingVariant
          programOptions={licensingProgramOptions}
          selectedPrograms={selectedPrograms}
        />
      )}

      <ViewBillingInfoModal
        transactionType={transaction.paymentMethod}
        date={reinterpretYearMonthDayAsLocalTime(transaction.date)}
        status={transaction.status}
        amount={transaction.amount}
        orderId={transaction.orderId}
        students={transaction.studentNames ?? []}
        isOpen={billingHistoryViewModalOpen}
        onClose={() => setBillingHistoryViewModalOpen(false)}
      />

      <ActionableTable
        isLoading={isLoading}
        rowActions={rowActions}
        columns={columns}
        noResultsMessage={noResultsMessage}
        customCssTable={{
          border: 'none',
        }}
        rowHeight={40}
        rows={rows}
        handlePaginationChange={({ page, pageSize }) => {
          setPage(page + 1)
          setPageSize(pageSize)
        }}
        pagination={{
          ...pagination,
          page: pagination.page - 1,
        }}
      />
    </>
  )
}

export default LcdLicensingPaymentHistory
